import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Badge, Button, Col, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LicenciasAsignadas from './LicenciasAsignadas';
import ModulosAsignados from './ModulosAsignados';
import PolizasAsignadas from './PolizasAsignadas';
import RenovacionesAsignadas from './RenovacionesAsignadas';
import PlanFacturacionAsociado from './PlanFacturacionAsociado';
import useToken from 'hooks/useToken';

/**
 * Componente que representa una fila de producto en la tabla de detalles de orden
 */
const ProductRow = ({
    product,
    orden,
    showEditarPrecios,
    editedProducts,
    handleProductChange,
    obtenerLicenciasParaModal,
    setseguroStateEliminar,
    idCatTipoUsuario,
    onPlanAsociado,
}) => {
    // Parsear licencias
    const objLicencias = JSON.parse(product.vJsonLicencias || '[]');
    const dNumeroLicencias = objLicencias.length;
    const dCantidadComprada = product.dCantidad;
    let dDiferencia = dCantidadComprada - dNumeroLicencias;

    // Rellenar con objetos vacíos si faltan licencias
    for (let i = 0; i < dDiferencia && dDiferencia > 0; i++) {
        objLicencias.push({
            idLicencia: 0,
            dInicioLicenciaUso: "",
            dFinLicenciaUso: "",
            Status: "",
            bIsActive: false
        });
    }

    // Parsear pólizas
    const objPolizas = JSON.parse(product.vJsonPolizas || '[]');
    let dNumeroPolizas = objPolizas.length;
    const dCantidadCompradaPoliza = product.dCantidad;
    let dDiferenciaPoliza = dCantidadCompradaPoliza - dNumeroPolizas;

    for (let i = 0; i < dDiferenciaPoliza && dDiferenciaPoliza > 0; i++) {
        objPolizas.push({ idPoliza: 0 });
    }

    // Parsear renovaciones
    const objLicRenov = JSON.parse(product.vJsonRenovacion || '[]');
    let dNumeroLicRenovada = objLicRenov.length;
    const dCantidadCompradaLicRenovada = product.dCantidad;
    let dDiferenciaLicRenovada = dCantidadCompradaLicRenovada - dNumeroLicRenovada;

    for (let i = 0; i < dDiferenciaLicRenovada && dDiferenciaLicRenovada > 0; i++) {
        objLicRenov.push({ idLicencia: 0 });
    }

    // Parsear módulos
    const objLicenciasModulo = JSON.parse(product.vJsonLicenciasModulos || '[]');
    let dNumeroLicRen = objLicenciasModulo.length;
    const dCantidadCompradaLicRen = product.dCantidad;
    let dDiferenciaLicRen = dCantidadCompradaLicRen - dNumeroLicRen;

    const esModuloExtra = String(product.vTipoProducto || '').toLowerCase() === 'moduloextra';

    // Rellenar con vacíos solo si no hay módulo asignado (ni en vJsonLicenciasModulos ni en vJsonLicencias para ModuloExtra)
    const tieneModuloAsignado = objLicenciasModulo.some(m => m.idLicencia > 0);
    const tieneModuloAsignadoViaLicencias = esModuloExtra && objLicencias.some(l => l.idLicencia > 0);
    if (!tieneModuloAsignado && !tieneModuloAsignadoViaLicencias) {
        for (let i = 0; i < dDiferenciaLicRen && dDiferenciaLicRen > 0; i++) {
            objLicenciasModulo.push({ idLicencia: 0 });
        }
    }

    const { token } = useToken();
    const [asignandoModulo, setAsignandoModulo] = useState(false);
    const [errorAsignarModulo, setErrorAsignarModulo] = useState(null);
    const [moduloYaAsignado, setModuloYaAsignado] = useState(null); // null=cargando, true/false=resultado
    const [idLicenciaAsignada, setIdLicenciaAsignada] = useState(null);

    const esModuloExtraConSuscripcion =
        esModuloExtra
        && !tieneModuloAsignado
        && Boolean(orden.idSuscripcion);

    React.useEffect(() => {
        if (!esModuloExtraConSuscripcion) return;

        fetch(`/api/Suscripciones/ValidarModuloDesdeOrden?idOrden=${product.idOrden}&idSuscripcion=${orden.idSuscripcion}`, {
            headers: { 'Authorization': 'Bearer ' + token }
        })
            .then(r => r.json())
            .then(data => {
                if (data.iResponseType === 1 && data.detalles) {
                    setModuloYaAsignado(data.detalles.bEstaAsignado === true || data.detalles.bEstaAsignado === 1);
                    setIdLicenciaAsignada(data.detalles.idLicencia || null);
                } else {
                    setModuloYaAsignado(false);
                    setIdLicenciaAsignada(null);
                }
            })
            .catch(() => {
                setModuloYaAsignado(false);
                setIdLicenciaAsignada(null);
            });
    }, [esModuloExtraConSuscripcion, product.idOrden, orden.idSuscripcion, token]);

    const puedeAsignarModulo = esModuloExtraConSuscripcion && moduloYaAsignado === false;

    const handleAsignarModulo = async () => {
        if (!orden.bIsPagada) {
            setErrorAsignarModulo('La orden debe estar pagada antes de asignar el módulo a la suscripción.');
            return;
        }
        setAsignandoModulo(true);
        setErrorAsignarModulo(null);
        try {
            const resp = await fetch('/api/Suscripciones/AsignarModuloDesdeOrden', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify({
                    idOrden: product.idOrden,
                    idSuscripcion: orden.idSuscripcion
                })
            });
            const data = await resp.json();
            if (data.iResponseType === 1) {
                window.location.reload();
            } else {
                setErrorAsignarModulo(data.vMessage || 'Error al asignar módulo');
            }
        } catch (e) {
            setErrorAsignarModulo('Error de red');
        } finally {
            setAsignandoModulo(false);
        }
    };

    return (
        <tr className="border-200">
            <td className="align-middle">
                {product.vImagenProducto_Base64 ? (
                    <img
                        src={`data:image/png;base64,${product.vImagenProducto_Base64}`}
                        alt={product.vNombreProducto}
                        style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                    />
                ) : (
                    <div
                        className="bg-light d-flex align-items-center justify-content-center"
                        style={{ width: '50px', height: '50px' }}
                    >
                        <FontAwesomeIcon icon="image" className="text-muted" />
                    </div>
                )}
            </td>
            <td className="align-middle">
                <p className="mb-0 text-nowrap">{product.vCodigoBarras}</p>
            </td>
            <td className="align-middle">
                <p className="mb-0">{product.vNombreProducto}</p>

                {/* Licencias Asignadas (no aplica para ModuloExtra) */}
                {!esModuloExtra && (
                    <Row>
                        <LicenciasAsignadas
                            objLicencias={objLicencias}
                            product={product}
                            orden={orden}
                            obtenerLicenciasParaModal={obtenerLicenciasParaModal}
                            setseguroStateEliminar={setseguroStateEliminar}
                            idCatTipoUsuario={idCatTipoUsuario}
                        />
                    </Row>
                )}

                {/* Pólizas Asignadas */}
                <Row>
                    <PolizasAsignadas
                        objPolizas={objPolizas}
                        product={product}
                    />
                </Row>

                {/* Renovaciones Asignadas */}
                <Row>
                    <RenovacionesAsignadas
                        objLicRenov={objLicRenov}
                        product={product}
                    />
                </Row>

                {/* Módulos Asignados */}
                {esModuloExtra && (
                    <Row>
                        <ModulosAsignados
                            objLicenciasModulo={objLicenciasModulo}
                            product={product}
                            orden={orden}
                        />
                        {esModuloExtraConSuscripcion && moduloYaAsignado === true && (
                            <Col xs="auto" className="mt-1 d-flex align-items-center gap-2">
                                <Badge bg="success">Ya asignado a suscripción</Badge>
                                {idLicenciaAsignada && (
                                    <a
                                        href={`/licencias/detalle/${idLicenciaAsignada}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ms-2 text-decoration-underline"
                                    >
                                        Licencia #{idLicenciaAsignada}
                                    </a>
                                )}
                            </Col>
                        )}
                        {puedeAsignarModulo && (
                            <Col xs="auto" className="mt-1">
                                <Button
                                    size="sm"
                                    variant="outline-warning"
                                    disabled={asignandoModulo}
                                    onClick={handleAsignarModulo}
                                >
                                    {asignandoModulo ? 'Asignando...' : 'Asignar a suscripción'}
                                </Button>
                                {errorAsignarModulo && (
                                    <div className="text-danger small mt-1">{errorAsignarModulo}</div>
                                )}
                            </Col>
                        )}
                    </Row>
                )}

                {/* Plan de Facturación Asociado */}
                {product.productoTimbres === 1 && (
                    <PlanFacturacionAsociado
                        product={product}
                        orden={orden}
                        onPlanAsociado={onPlanAsociado}
                    />
                )}
            </td>

            {/* Cantidad */}
            <td className="align-middle text-center">
                {showEditarPrecios && orden.bSePuedeEditarPrecios ? (
                    <Form.Control
                        type="number"
                        size="sm"
                        min="1"
                        step="1"
                        value={editedProducts[product.idOrdenDetalle]?.dCantidad || product.dCantidad}
                        onChange={(e) => handleProductChange(product.idOrdenDetalle, 'dCantidad', e.target.value)}
                        style={{ width: '80px', textAlign: 'center' }}
                    />
                ) : (
                    product.dCantidad
                )}
            </td>

            {/* Precio Unitario */}
            <td className="align-middle text-end">
                {showEditarPrecios && orden.bSePuedeEditarPrecios ? (
                    <Form.Control
                        type="number"
                        size="sm"
                        min="0"
                        step="0.01"
                        value={editedProducts[product.idOrdenDetalle]?.dPrecioUnitario || parseFloat((product.dImporte - product.dDescuento + product.dIVA) / product.dCantidad).toFixed(2)}
                        onChange={(e) => handleProductChange(product.idOrdenDetalle, 'dPrecioUnitario', e.target.value)}
                        style={{ width: '120px' }}
                    />
                ) : (
                    parseFloat((product.dImporte - product.dDescuento + product.dIVA) / product.dCantidad).toLocaleString("es-MX", {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: 'currency',
                        currency: 'MXN'
                    })
                )}
            </td>

            {/* Importe */}
            <td className="align-middle text-end">
                {showEditarPrecios && orden.bSePuedeEditarPrecios ? (
                    <div>
                        <div className="text-primary fw-bold">
                            ${parseFloat(editedProducts[product.idOrdenDetalle]?.dImporte || product.dImporte).toLocaleString("es-MX", {
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2
                            })}
                        </div>
                        {(editedProducts[product.idOrdenDetalle]?.dDescuento || product.dDescuento) > 0 && (
                            <Badge bg="danger" size="sm" className="mt-1">
                                -${parseFloat(editedProducts[product.idOrdenDetalle]?.dDescuento || product.dDescuento).toLocaleString("es-MX", {
                                    maximumFractionDigits: 2,
                                    minimumFractionDigits: 2
                                })}
                            </Badge>
                        )}
                    </div>
                ) : (
                    <div>
                        {parseFloat(product.dImporte).toLocaleString("es-MX", {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                            style: 'currency',
                            currency: 'MXN'
                        })}
                        {product.dDescuento > 0 && (
                            <>
                                <br />
                                <Badge bg="danger">
                                    -{parseFloat(product.dDescuento).toLocaleString("es-MX", {
                                        maximumFractionDigits: 2,
                                        minimumFractionDigits: 2,
                                        style: 'currency',
                                        currency: 'MXN'
                                    })}
                                </Badge>
                            </>
                        )}
                    </div>
                )}
            </td>

            {/* IVA */}
            <td className="align-middle text-end">
                {showEditarPrecios && orden.bSePuedeEditarPrecios ? (
                    <div className="text-info fw-bold">
                        ${parseFloat(editedProducts[product.idOrdenDetalle]?.dIVA || product.dIVA).toLocaleString("es-MX", {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2
                        })}
                    </div>
                ) : (
                    parseFloat(product.dIVA).toLocaleString("es-MX", {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: 'currency',
                        currency: 'MXN'
                    })
                )}
            </td>

            {/* Total */}
            <td className="align-middle text-end">
                {showEditarPrecios && orden.bSePuedeEditarPrecios ? (
                    <div className="text-success fw-bold">
                        ${parseFloat((editedProducts[product.idOrdenDetalle]?.dImporte || product.dImporte) + (editedProducts[product.idOrdenDetalle]?.dIVA || product.dIVA)).toLocaleString("es-MX", {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2
                        })}
                    </div>
                ) : (
                    parseFloat(product.dTotal).toLocaleString("es-MX", {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: 'currency',
                        currency: 'MXN'
                    })
                )}
            </td>
        </tr>
    );
};

ProductRow.propTypes = {
    product: PropTypes.object.isRequired,
    orden: PropTypes.object.isRequired,
    showEditarPrecios: PropTypes.bool.isRequired,
    editedProducts: PropTypes.object.isRequired,
    handleProductChange: PropTypes.func.isRequired,
    obtenerLicenciasParaModal: PropTypes.func.isRequired,
    setseguroStateEliminar: PropTypes.func.isRequired,
    idCatTipoUsuario: PropTypes.string.isRequired,
    onPlanAsociado: PropTypes.func
};

export default ProductRow;
